import { Download, FileSpreadsheet, Upload } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { Button, Card, PageHeader } from "../components/ui";
import { api, downloadFile, errMsg } from "../lib/api";

export default function ImportExport() {
  const [file, setFile] = useState<File | null>(null);
  const [report, setReport] = useState<any>(null);
  const [busy, setBusy] = useState(false);

  const upload = async () => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/io/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
      toast.success(`Import selesai: ${data.students.inserted} siswa, ${data.loans.inserted} pinjaman`);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const exp = (kind: string) => downloadFile(`/io/export/${kind}`, `export_${kind}.xlsx`).catch((e) => toast.error(errMsg(e)));

  return (
    <div data-testid="import-export-page">
      <PageHeader title="Import / Export" subtitle="Template Excel · import master siswa + pinjaman aktif · export laporan" />
      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Import Master Data" subtitle="Unduh template, isi sheet Siswa & Pinjaman, lalu unggah (.xlsx / .csv)" testId="import-card">
          <Button variant="outline" data-testid="template-download-button" onClick={() => downloadFile("/io/template", "template_yutaka_edu_finance.xlsx")}><FileSpreadsheet className="h-4 w-4" /> Unduh Template</Button>
          <div className="mt-4 rounded-lg border-2 border-dashed p-5 text-center">
            <input data-testid="import-file-input" type="file" accept=".xlsx,.csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="text-sm" />
            <p className="text-xs text-muted-foreground mt-2">Baris yang gagal validasi (NIK bukan 16 digit, duplikat, siswa sudah punya pinjaman aktif) akan dilaporkan dan dilewati.</p>
          </div>
          <Button className="mt-3" disabled={!file || busy} onClick={upload} data-testid="import-submit-button"><Upload className="h-4 w-4" /> {busy ? "Mengimpor…" : "Import"}</Button>
          {report && (
            <div className="mt-4 text-sm space-y-2" data-testid="import-report">
              <p><b>Siswa:</b> {report.students.inserted} berhasil · {report.students.errors.length} gagal</p>
              <p><b>Pinjaman:</b> {report.loans.inserted} berhasil · {report.loans.errors.length} gagal</p>
              {[...report.students.errors.map((e: any) => ({ ...e, sheet: "Siswa" })), ...report.loans.errors.map((e: any) => ({ ...e, sheet: "Pinjaman" }))].length > 0 && (
                <div className="max-h-56 overflow-auto rounded border">
                  <table className="yt-table">
                    <thead><tr><th>Sheet</th><th>Baris</th><th>NIK</th><th>Error</th></tr></thead>
                    <tbody>
                      {[...report.students.errors.map((e: any) => ({ ...e, sheet: "Siswa" })), ...report.loans.errors.map((e: any) => ({ ...e, sheet: "Pinjaman" }))].map((e: any, i: number) => (
                        <tr key={i}><td>{e.sheet}</td><td className="num">{e.row}</td><td className="num">{e.nik}</td><td className="text-red-600">{e.error}</td></tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </Card>
        <Card title="Export Laporan" subtitle="Format Excel (.xlsx), siap untuk audit dan rekonsiliasi" testId="export-card">
          <div className="grid gap-2">
            {[["students", "Master Siswa"], ["loans", "Pinjaman & Outstanding"], ["payments", "Riwayat Pembayaran"], ["npl", "Kolektibilitas / NPL (indikatif)"]].map(([k, l]) => (
              <Button key={k} variant="outline" className="justify-between" data-testid={`export-${k}-button`} onClick={() => exp(k)}>{l} <Download className="h-4 w-4" /></Button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
